import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { StreamChat } from 'stream-chat';
import { streamChatApi } from '../api/streamChatApi';
import useAuth from '../hooks/useAuth';
import { getUserProfileFullName } from '../utils/users';
import { User } from '../graphql/types';

interface StreamChatContextValue {
  client: StreamChat | null;
  isConnected: boolean;
  isLoading: boolean;
}

const StreamChatContext = createContext<StreamChatContextValue>({
  client: null,
  isConnected: false,
  isLoading: true
});

interface StreamChatProviderProps {
  children: ReactNode;
}

export const StreamChatProvider: React.FC<StreamChatProviderProps> = ({ children }) => {
  const { user: currentUser, isLoggedIn } = useAuth();
  const [client, setClient] = useState<StreamChat | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let streamClient: StreamChat | null = null;
    let mounted = true;

    const initializeStreamChat = async () => {
      if (!currentUser || !isLoggedIn) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        const { token, apiKey } = await streamChatApi.getToken();
        streamClient = StreamChat.getInstance(apiKey);

        await streamClient.connectUser(
          {
            id: currentUser.id,
            name: getUserProfileFullName(currentUser as Partial<User>) || currentUser.id
          },
          token
        );

        if (mounted) {
          setClient(streamClient);
          setIsConnected(true);
        }
      } catch (error) {
        if (mounted) {
          setClient(null);
          setIsConnected(false);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    initializeStreamChat();

    return () => {
      mounted = false;
      if (streamClient) {
        streamClient.disconnectUser();
      }
    };
  }, [currentUser?.id, isLoggedIn]);

  const value: StreamChatContextValue = {
    client,
    isConnected,
    isLoading
  };

  return <StreamChatContext.Provider value={value}>{children}</StreamChatContext.Provider>;
};

export const useStreamChat = (): StreamChatContextValue => {
  const context = useContext(StreamChatContext);
  if (!context) {
    throw new Error('useStreamChat must be used within a StreamChatProvider');
  }
  return context;
};
